import type { PlanEvent } from "../../../shared/types";
import { addDays } from "../../../shared/types/date-helpers";

export function sortEvents(events: PlanEvent[]): PlanEvent[] {
  return [...events].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.startTime.localeCompare(b.startTime);
  });
}

function getDayOfWeek(dateStr: string): number {
  return new Date(dateStr + "T00:00:00").getDay();
}

function getStartOfWeek(dateStr: string): string {
  const dow = getDayOfWeek(dateStr);
  return addDays(dateStr, -dow);
}

function expandWeeklyWithDays(
  event: PlanEvent,
  startDate: string,
  endDate: string,
  instances: PlanEvent[],
) {
  const rule = event.recurrence!;
  const days = rule.daysOfWeek!;
  let weekStart = getStartOfWeek(event.date);

  while (weekStart <= endDate) {
    for (const dow of days) {
      const current = addDays(weekStart, dow);
      if (current < event.date) continue;
      if (current > endDate) break;
      if (rule.endDate && current > rule.endDate) break;
      if (current >= startDate) {
        instances.push({ ...event, date: current });
      }
    }
    if (rule.endDate && weekStart > rule.endDate) break;
    weekStart = addDays(weekStart, 7 * rule.interval);
  }
}

export function generateRecurringInstances(
  events: PlanEvent[],
  startDate: string,
  endDate: string,
): PlanEvent[] {
  const instances: PlanEvent[] = [];
  for (const event of events) {
    if (!event.recurrence) {
      if (event.date >= startDate && event.date <= endDate) {
        instances.push(event);
      }
      continue;
    }
    const rule = event.recurrence;

    if (rule.type === "weekly" && rule.daysOfWeek && rule.daysOfWeek.length > 0) {
      expandWeeklyWithDays(event, startDate, endDate, instances);
      continue;
    }

    let current = event.date;
    while (current <= endDate) {
      if (current >= startDate) {
        instances.push({ ...event, date: current });
      }
      if (rule.endDate && current > rule.endDate) break;
      switch (rule.type) {
        case "daily":
          current = addDays(current, rule.interval);
          break;
        case "weekly":
          current = addDays(current, 7 * rule.interval);
          break;
        case "monthly": {
          const d = new Date(current + "T00:00:00");
          d.setMonth(d.getMonth() + rule.interval);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          current = `${y}-${m}-${day}`;
          break;
        }
      }
    }
  }
  return sortEvents(instances);
}
